import dayjs from "../config/dayjs";

export const getCurrentGulfDateTime = () => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZone: "Asia/Dubai", year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };

  const gulfDateTime = date.toLocaleString("en-US", options);

  const [month, day, year, hour, minute] = gulfDateTime.split(/[/,\s:]+/);
  const formattedDateTime = `${year}-${month}-${day}T${hour}:${minute}`;

  return formattedDateTime;
}

const formatOrEmpty = (input: string | Date | null | undefined, pattern: string): string => {
  if (!input) return "";
  const parsed = dayjs(input);
  return parsed.isValid() ? parsed.format(pattern) : "";
};

export const formatDateTime = (dateString: string | Date, onlyDate: boolean = false): string => {
  return formatOrEmpty(dateString, onlyDate ? "DD-MM-YYYY" : "DD-MM-YYYY HH:mm:ss");
}

// "01 Sep 2026"
export const formatDay = (date: Date | string) => formatOrEmpty(date, "DD MMM YYYY");

// "2026-11" -> "November 2026"
export const formatMonthLabel = (month: string) => formatOrEmpty(`${month}-01`, "MMMM YYYY");

// "Tue, 01 Sep 2026"
export const formatDayWithWeekday = (date: Date | string) => formatOrEmpty(date, "ddd, DD MMM YYYY");

export const formatDateLocal = (date: Date) => {
  return formatOrEmpty(date, "YYYY-MM-DD"); // returns "YYYY-MM-DD"
};


export const formatUTCDateToLocal = (dateString: string | Date, onlyDate: boolean = false): string => {
  if (!dateString) return "";
  const parsed = dayjs(dateString).utc();
  if (!parsed.isValid()) return "";
  return parsed.format(onlyDate ? "DD-MM-YYYY" : "DD-MM-YYYY HH:mm:ss");
}

export const formatDateTime12Hours = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export const formatTimeDiff = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${days > 0 ? `${days} Days, ` : ''}${hours} Hours, ${minutes} Min, ${seconds} Sec`;
};

export const formatDuration = (timeDiffMs: number): string => {
  const totalSeconds = Math.floor(timeDiffMs / 1000); // convert ms → seconds
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  // if all are zero (very short duration), show 0s
  if (parts.length === 0) parts.push('0s');

  return parts.join(' ');
}
export const formatDate = (date: Date): string => {
  return formatOrEmpty(date, "YYYYMMDD");
};

export const initialDateRange = {
  startDate: (() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return startDate;
  })(),
  endDate: new Date(),
  key: "selection",
}

export const initialCashbackDateRange = {
  startDate: (() => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    return startDate;
  })(),
  endDate: new Date(),
  key: "selection",
}

export const formatLastLogin = (dateStr: string) => {
  const date = new Date(dateStr);

  // Format: Nov 10, 2025 at 10:30 AM
  const formattedDate = date.toLocaleDateString('en-AE', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const formattedTime = date.toLocaleTimeString('en-AE', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return `${formattedDate} at ${formattedTime}`;
};

export const toUAETime = (input: Date | string): string => {
  return dayjs(input).utc()
    .format("DD MMM YYYY, hh:mm:ss A");
};

export const shiftDate = (date: string, days: number): string =>
  dayjs.utc(date).add(days, "day").format("YYYY-MM-DD");

export const toLocalTime = (input: Date | string): string => {
  return dayjs(input).format("DD MMM YYYY, hh:mm:ss A");
};

export const formatChatTime = (date: string): string => formatOrEmpty(date, "hh:mm A");

export const formatChatDay = (date: string): string => {
  const day = dayjs(date);
  if (day.isSame(dayjs(), "day")) return "Today";
  if (day.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return formatOrEmpty(date, "DD MMM YYYY");
};

export const formatChatStamp = (date: string): string =>
  dayjs(date).isSame(dayjs(), "day") ? formatChatTime(date) : formatChatDay(date);

export const formatChatLastSeen = (date: string): string => {
  const day = formatChatDay(date);
  return `${day === "Today" || day === "Yesterday" ? day.toLowerCase() : day} at ${formatChatTime(date)}`;
};